import { useCallback, useEffect, useState } from 'react';
import { applyTheme, currentTheme, getThemeSetting, setThemeSetting, THEME_CHANGED, watchTheme, type Theme, type ThemeSetting } from '../utils/theme';

/**
 * The theme setting (light, dark, system) and the theme in force, for the settings page and the
 * account menu. One hook instance (App) also keeps the page in step with the system.
 */
export const useTheme = (): { setting: ThemeSetting; theme: Theme; setSetting: (setting: ThemeSetting) => void } => {
    const [setting, setSettingState] = useState<ThemeSetting>(getThemeSetting);
    const [theme, setTheme] = useState<Theme>(currentTheme);

    useEffect(() => {
        const onChange = (event: Event) => {
            setTheme((event as CustomEvent<Theme>).detail);
            setSettingState(getThemeSetting());
        };
        window.addEventListener(THEME_CHANGED, onChange);
        return () => window.removeEventListener(THEME_CHANGED, onChange);
    }, []);

    const setSetting = useCallback((next: ThemeSetting) => {
        setSettingState(next);
        setTheme(setThemeSetting(next));
    }, []);

    return { setting, theme, setSetting };
};

/** Mounted once, at the top of the app: applies the stored setting and follows the system. */
export const useThemeWatcher = (): void => {
    useEffect(() => {
        applyTheme();
        return watchTheme();
    }, []);
};
